import React, { useState, useRef, useEffect } from 'react';
import { Paperclip, Search, Square, ChevronDown, Zap, X, ChevronUp, Copy, Check, FileText, FileSpreadsheet, Download, GitCompareArrows } from 'lucide-react';
import { Logo } from '../../common/Logo';
import { Dropdown } from '../../ui';
import type { DropdownOption } from '../../ui';
import { api, AIModel, skillsApi } from '../../../services/api';
import { exportService } from '../../../services/exportService';
import { MultiModelComparison } from '../MultiModelComparison';
import type { ModelResponse } from '../MultiModelComparison';
import { ClarificationCard, parseClarificationQuestions } from '../ClarificationCard';
import type { Skill } from '../../../types';
import styles from './ChatArea.module.css';

export interface ConversationStarter {
  id: string;
  text: string;
}

export interface MessageItem {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt?: string;
}

export interface ChatAreaProps {
  selectedAssistant?: {
    id: string;
    name: string;
    icon?: string;
    conversationStarters?: ConversationStarter[];
  };
  messages?: MessageItem[];
  streamingContent?: string;
  isStreaming?: boolean;
  isLoading?: boolean;
  toolStatus?: string | null;
  onSendMessage: (message: string, skillIds?: string[]) => void;
  onCancelStream?: () => void;
  onStarterClick?: (starter: ConversationStarter) => void;
  isNewChat?: boolean;
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  /** Multi-model comparison state (controlled by parent) */
  isCompareMode?: boolean;
  onCompareModeChange?: (enabled: boolean) => void;
  compareModels?: string[];
  onCompareModelsChange?: (models: string[]) => void;
  multiModelResponses?: Record<string, ModelResponse>;
  isComparing?: boolean;
  selectedCompareModel?: string | null;
  onSelectCompareModel?: (model: string) => void;
}

/** Render message content with inline clarification cards */
const MessageContent: React.FC<{
  content: string;
  onClarificationSubmit: (answers: Record<string, string>) => void;
}> = ({ content, onClarificationSubmit }) => {
  const { hasClarification, questions, beforeText, afterText } = parseClarificationQuestions(content);

  if (!hasClarification) {
    return <div className={styles.messageText}>{content}</div>;
  }

  return (
    <div className={styles.messageText}>
      {beforeText && <div>{beforeText}</div>}
      <ClarificationCard questions={questions} onSubmit={onClarificationSubmit} />
      {afterText && <div>{afterText}</div>}
    </div>
  );
};

export const ChatArea: React.FC<ChatAreaProps> = ({
  selectedAssistant,
  messages = [],
  streamingContent = '',
  isStreaming = false,
  isLoading = false,
  toolStatus,
  onSendMessage,
  onCancelStream,
  onStarterClick,
  isNewChat = true,
  selectedModel,
  onModelChange,
  isCompareMode = false,
  onCompareModeChange,
  compareModels = [],
  onCompareModelsChange,
  multiModelResponses = {},
  isComparing = false,
  selectedCompareModel = null,
  onSelectCompareModel,
}) => {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [models, setModels] = useState<DropdownOption[]>([]);
  const [loadingModels, setLoadingModels] = useState(true);

  // Skill picker state
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [isSkillsOpen, setIsSkillsOpen] = useState(false);

  // Message action state
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [exportingMessageId, setExportingMessageId] = useState<string | null>(null);

  /** Handle clarification answers — send them back as a user message */
  const handleClarificationSubmit = (answers: Record<string, string>) => {
    const formattedAnswers = Object.entries(answers)
      .map(([_, value]) => value)
      .join('\n');
    onSendMessage(formattedAnswers);
  };

  useEffect(() => {
    loadModels();
    loadSkills();
  }, []);

  const loadSkills = async () => {
    try {
      const response = await skillsApi.list();
      if (response.success && response.data) {
        const skills = Array.isArray(response.data) ? response.data : (response.data.skills || []);
        setAvailableSkills(skills);
      }
    } catch (error) {
      console.error('Failed to load skills:', error);
    }
  };

  const toggleSkill = (skillId: string) => {
    setSelectedSkillIds(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const removeSkill = (skillId: string) => {
    setSelectedSkillIds(prev => prev.filter(id => id !== skillId));
  };

  const loadModels = async () => {
    try {
      setLoadingModels(true);
      const response = await api.getAIModels();
      if (response.success && response.data && response.data.models) {
        const modelOptions: DropdownOption[] = response.data.models.map((model: AIModel) => ({
          value: model.id,
          label: model.name,
          subtitle: `${model.provider.toUpperCase()} • ${model.publisher}`,
        }));
        setModels(modelOptions);
        
        if (modelOptions.length > 0 && onModelChange) {
          // Set default when empty OR switch away from stale model IDs no longer available.
          const hasSelected = !!selectedModel && modelOptions.some(m => m.value === selectedModel);
          if (!hasSelected) {
            onModelChange(modelOptions[0].value);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load models:', error);
      // Fallback to default models
      setModels([
        { value: 'gpt-4o', label: 'GPT-4o', subtitle: 'GITHUB • Azure OpenAI Service' },
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini', subtitle: 'GITHUB • Azure OpenAI Service' },
      ]);
    } finally {
      setLoadingModels(false);
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isStreaming && !isLoading) {
      onSendMessage(message.trim(), selectedSkillIds.length > 0 ? selectedSkillIds : undefined);
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleStopStream = () => {
    onCancelStream?.();
  };

  /** Copy message content to clipboard */
  const handleCopyMessage = async (msgId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(msgId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  /** Export assistant message as DOCX */
  const handleExportDocx = async (msgId: string, content: string) => {
    try {
      setExportingMessageId(msgId);
      await exportService.exportAsDocx({ content });
    } catch (err: any) {
      console.error('Failed to export DOCX:', err);
      const message = err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')
        ? 'Cannot reach the server. Please make sure the backend is running and try again.'
        : `Export failed: ${err.message || 'Unknown error'}. Please try again.`;
      alert(message);
    } finally {
      setExportingMessageId(null);
    }
  };

  /** Export assistant message as XLSX */
  const handleExportXlsx = async (msgId: string, content: string) => {
    try {
      setExportingMessageId(msgId);
      await exportService.exportAsXlsx({ content });
    } catch (err: any) {
      console.error('Failed to export XLSX:', err);
      const message = err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')
        ? 'Cannot reach the server. Please make sure the backend is running and try again.'
        : `Export failed: ${err.message || 'Unknown error'}. Please try again.`;
      alert(message);
    } finally {
      setExportingMessageId(null);
    }
  };

  /** Export assistant message as Markdown */
  const handleExportMarkdown = (content: string) => {
    exportService.exportAsMarkdown({ content });
  };

  const defaultStarters: ConversationStarter[] = [
    { id: '1', text: 'Tell me about BrainIQ platform.' },
    { id: '2', text: 'How to set up Jira integration in BrainIQ?' },
    { id: '3', text: 'How to create assistant in BrainIQ platform?' },
  ];

  const starters = selectedAssistant?.conversationStarters || defaultStarters;

  /** Build model display name map from loaded models */
  const modelDisplayNames: Record<string, string> = {};
  models.forEach(m => { modelDisplayNames[m.value] = m.label; });

  /** Toggle a model in the compare list */
  const toggleCompareModel = (modelId: string) => {
    if (!onCompareModelsChange) return;
    const updated = compareModels.includes(modelId)
      ? compareModels.filter(m => m !== modelId)
      : [...compareModels, modelId];
    onCompareModelsChange(updated);
  };

  return (
    <div className={styles.chatArea}>
      <div className={styles.chatHeader}>
        <div className={styles.headerLeft}>
          {onCompareModeChange && (
            <button
              className={`${styles.compareToggle} ${isCompareMode ? styles.compareToggleActive : ''}`}
              onClick={() => onCompareModeChange(!isCompareMode)}
              title={isCompareMode ? 'Switch to single model' : 'Compare multiple models'}
            >
              <GitCompareArrows size={16} />
              {isCompareMode ? 'Comparing' : 'Compare'}
            </button>
          )}
        </div>
        <div className={styles.modelSelector}>
          {!isCompareMode && onModelChange && (
            <Dropdown
              options={models}
              value={selectedModel || ''}
              onChange={onModelChange}
              placeholder="Select a model"
              disabled={loadingModels}
              className={styles.modelDropdown}
            />
          )}
        </div>
      </div>

      {/* Multi-model selector chips when compare mode is on */}
      {isCompareMode && (
        <div className={styles.compareModelSelector}>
          <span className={styles.compareLabel}>Select models to compare:</span>
          <div className={styles.compareChips}>
            {models.map(m => {
              const isChosen = compareModels.includes(m.value);
              return (
                <button
                  key={m.value}
                  className={`${styles.compareChip} ${isChosen ? styles.compareChipActive : ''}`}
                  onClick={() => toggleCompareModel(m.value)}
                >
                  {m.label}
                  {isChosen && <Check size={12} />}
                </button>
              );
            })}
          </div>
          {compareModels.length < 2 && (
            <span className={styles.compareHint}>Pick at least 2 models</span>
          )}
        </div>
      )}
      {isNewChat && messages.length === 0 && !isLoading ? (
        <div className={styles.welcomeSection}>
          <div className={styles.welcomeIcon}>
            <Logo size="lg" />
          </div>
          
          <h1 className={styles.welcomeTitle}>
            {selectedAssistant
              ? 'Hi there, how can we help today?'
              : 'Start a conversation with an assistant'}
          </h1>
          
          <p className={styles.welcomeSubtitle}>
            {selectedAssistant
              ? 'Your assistants are here to help with anything you need'
              : "Ask anything or type @ and choose assistant from the list"}
          </p>

          <div className={styles.starters}>
            {starters.map((starter) => (
              <button
                key={starter.id}
                className={styles.starterButton}
                onClick={() => onStarterClick?.(starter)}
              >
                {starter.text}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.messagesSection}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`${styles.message} ${styles[msg.role]}`}
            >
              <div className={styles.messageAvatar}>
                {msg.role === 'user' ? '👤' : '🤖'}
              </div>
              <div className={styles.messageContent}>
                <div className={styles.messageRole}>
                  {msg.role === 'user' ? 'You' : selectedAssistant?.name || 'Assistant'}
                </div>
                {msg.role === 'assistant' ? (
                  <MessageContent
                    content={msg.content}
                    onClarificationSubmit={handleClarificationSubmit}
                  />
                ) : (
                  <div className={styles.messageText}>{msg.content}</div>
                )}

                {/* Action bar for all messages */}
                <div className={styles.messageActions}>
                  <button
                    className={`${styles.messageActionBtn} ${copiedMessageId === msg.id ? styles.messageActionActive : ''}`}
                    onClick={() => handleCopyMessage(msg.id, msg.content)}
                    title="Copy message"
                  >
                    {copiedMessageId === msg.id ? <Check size={14} /> : <Copy size={14} />}
                    <span>{copiedMessageId === msg.id ? 'Copied' : 'Copy'}</span>
                  </button>

                  {/* Download options — only for assistant messages */}
                  {msg.role === 'assistant' && (
                    <>
                      <div className={styles.messageActionDivider} />
                      <button
                        className={styles.messageActionBtn}
                        onClick={() => handleExportDocx(msg.id, msg.content)}
                        disabled={exportingMessageId === msg.id}
                        title="Download as Word document"
                      >
                        <FileText size={14} />
                        <span>{exportingMessageId === msg.id ? 'Exporting...' : '.docx'}</span>
                      </button>
                      <button
                        className={styles.messageActionBtn}
                        onClick={() => handleExportXlsx(msg.id, msg.content)}
                        disabled={exportingMessageId === msg.id}
                        title="Download as Excel spreadsheet"
                      >
                        <FileSpreadsheet size={14} />
                        <span>.xlsx</span>
                      </button>
                      <button
                        className={styles.messageActionBtn}
                        onClick={() => handleExportMarkdown(msg.content)}
                        title="Download as Markdown"
                      >
                        <Download size={14} />
                        <span>.md</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {/* Streaming message */}
          {isStreaming && (
            <div className={`${styles.message} ${styles.assistant}`}>
              <div className={styles.messageAvatar}>🤖</div>
              <div className={styles.messageContent}>
                <div className={styles.messageRole}>
                  {selectedAssistant?.name || 'Assistant'}
                </div>
                <div className={styles.messageText}>
                  {streamingContent || ''}
                  <span className={styles.cursor}>▊</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Loading indicator - Show tool status or "Assistant is thinking..." */}
          {isLoading && !isStreaming && !isComparing && (
            <div className={`${styles.message} ${styles.assistant}`}>
              <div className={styles.messageAvatar}>🤖</div>
              <div className={styles.messageContent}>
                <div className={styles.messageRole}>
                  {selectedAssistant?.name || 'Assistant'}
                </div>
                <div className={styles.thinkingIndicator}>
                  <span className={styles.thinkingText}>
                    {toolStatus || `${selectedAssistant?.name || 'Assistant'} is thinking`}
                  </span>
                  {!toolStatus && (
                    <span className={styles.thinkingDots}>
                      <span></span><span></span><span></span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Multi-model comparison cards */}
          {Object.keys(multiModelResponses).length > 0 && (
            <MultiModelComparison
              responses={multiModelResponses}
              selectedModel={selectedCompareModel || null}
              onSelectModel={(model) => onSelectCompareModel?.(model)}
              isComparing={isComparing}
              modelDisplayNames={modelDisplayNames}
            />
          )}
          
          <div ref={messagesEndRef} />
        </div>
      )}

      <div className={styles.inputSection}>
        {/* Selected skills pills */}
        {selectedSkillIds.length > 0 && (
          <div className={styles.selectedSkills}>
            {selectedSkillIds.map(id => {
              const skill = availableSkills.find(s => s.id === id);
              if (!skill) return null;
              return (
                <span key={id} className={styles.skillPill}>
                  <Zap size={12} />
                  {skill.displayName}
                  <button
                    type="button"
                    className={styles.skillPillRemove}
                    onClick={() => removeSkill(id)}
                  >
                    <X size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <form className={styles.inputForm} onSubmit={handleSubmit}>
          <div className={styles.inputWrapper}>
            <textarea
              className={styles.input}
              placeholder="Ask anything or add an assistant with '@'"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <div className={styles.inputActions}>
              <button
                type="button"
                className={`${styles.actionButton} ${isSkillsOpen ? styles.actionButtonActive : ''}`}
                title="Select skills"
                onClick={() => setIsSkillsOpen(!isSkillsOpen)}
              >
                <Zap size={18} />
              </button>
              <button
                type="button"
                className={styles.actionButton}
                title="Attach file"
              >
                <Paperclip size={18} />
              </button>
              <button
                type="button"
                className={styles.actionButton}
                title="Web search"
              >
                <Search size={18} />
              </button>
              <div className={styles.divider} />
              {isStreaming ? (
                <button
                  type="button"
                  className={styles.stopButton}
                  onClick={handleStopStream}
                  title="Stop generating"
                >
                  <Square size={14} />
                  Stop
                </button>
              ) : (
                <button
                  type="submit"
                  className={styles.sendButton}
                  disabled={!message.trim() || isLoading}
                  title="Send message"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Skill picker dropdown */}
        {isSkillsOpen && (
          <div className={styles.skillPickerDropdown}>
            <div className={styles.skillPickerHeader}>
              <span className={styles.skillPickerTitle}>
                <Zap size={14} />
                Select Skills
              </span>
              <button
                type="button"
                className={styles.skillPickerClose}
                onClick={() => setIsSkillsOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className={styles.skillPickerList}>
              {availableSkills.length === 0 ? (
                <div className={styles.skillPickerEmpty}>No skills available</div>
              ) : (
                availableSkills.map(skill => (
                  <button
                    key={skill.id}
                    type="button"
                    className={`${styles.skillPickerItem} ${selectedSkillIds.includes(skill.id) ? styles.skillPickerItemActive : ''}`}
                    onClick={() => toggleSkill(skill.id)}
                  >
                    <div className={styles.skillPickerItemInfo}>
                      <span className={styles.skillPickerItemName}>{skill.displayName}</span>
                      <span className={styles.skillPickerItemDesc}>{skill.description}</span>
                    </div>
                    {selectedSkillIds.includes(skill.id) && (
                      <span className={styles.skillPickerCheck}>✓</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatArea;
